// @file Slideshow store
import { isAppUsing } from '@@/bits/flip'
import { getSearchParam, isSearchParamTruthy } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import { getNumberOfWords } from '@@/bits/post_processing'
import { buildSlideshowLink, COVER_PAGE_INDEX, getCurrentPostCid } from '@@/bits/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Cid } from '@@/types'
import type { Page } from '@@/types/slideshow'
import { PageType } from '@@/types/slideshow'
import type { RootState } from '@@/vuexstore/surface/types'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSlideshowStore = defineStore('slideshow', () => {
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceStore = useSurfaceStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfaceUserContributorsStore = useSurfaceUserContributorsStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()

  /*
   * DATA
   */

  const hasLoadedPages = ref(false)
  const isDataLoaded = computed(() => {
    if (!surfacePostsStore.hasFetchedPosts) return false
    if (surfaceWishArrangementStore.isGroupBySection && !surfaceSectionsStore.hasFetchedSections) return false
    if (surfaceWishArrangementStore.isGroupByAuthor && !surfaceUserContributorsStore.areAuthorsFetched) return false
    if (
      surfaceWishArrangementStore.isGroupBySingleSelect &&
      !surfacePostPropertiesStore.hasFetchedInitialCustomPostProperties
    )
      return false

    return true
  })
  const pageCoverLoaded = ref(false)
  const isPageCoverLoaded = computed(() => {
    if (currentPage.value?.pageType !== PageType.Cover) return true
    if (shouldRemoveBackground.value) return true
    return pageCoverLoaded.value
  })

  /*
   * CUSTOM STYLES
   */

  const shouldRemoveBackground = computed(() => {
    const shouldRemoveBackground = getSearchParam('remove_background')
    return isSearchParamTruthy(shouldRemoveBackground)
  })

  /*
   * MENUS
   */

  const isMoreMenuOpen = ref(false)
  const isAutoplayMenuOpen = ref(false)
  const isAnyMenuOpen = computed(() => isMoreMenuOpen.value || isAutoplayMenuOpen.value)

  /*
   * PANELS
   */

  const isSharePanelOpen = ref(false)
  const isShareViaAnotherAppPanelOpen = ref(false)
  const isEmbedPanelOpen = ref(false)
  const isLanguagePanelOpen = ref(false)
  const isAccessibilitySettingsModalOpen = ref(false)
  const isAnyPanelOpen = computed(
    () =>
      isSharePanelOpen.value ||
      isShareViaAnotherAppPanelOpen.value ||
      isEmbedPanelOpen.value ||
      isLanguagePanelOpen.value ||
      isAccessibilitySettingsModalOpen.value,
  )
  const isAnyOverlayOpen = computed(() => isAnyMenuOpen.value || isAnyPanelOpen.value)

  /*
   * QR CODE
   */

  const isQrCodeShown = ref(true)

  const shouldRemoveQrCode = computed(() => {
    const removeQrCodeParam = getSearchParam('remove_qr_code')
    return isSearchParamTruthy(removeQrCodeParam)
  })

  /*
   * PAGES
   */

  // We initialize this inside SurfaceSlideshow once the slideshow data has loaded
  // It's wrong to initialize this with a value before any page data exists
  const currentPageIndex = ref<number | null>(null)

  const pages = computed(() => {
    // Prevent pages from being populated when not all data is available yet
    if (!isDataLoaded.value) {
      return []
    }

    // Populate pages from posts and/or groups
    const populatedPages: Page[] = [
      {
        pageType: PageType.Cover,
      },
    ]

    const pushGroupPage = (groupId: number | string): void => {
      const pageType = isAppUsing('groupPostsV2') ? PageType.Group : PageType.Section

      populatedPages.push({
        pageType,
        entityId: groupId.toString(),
      })
    }

    const pushPostPage = (postCid: string): void => {
      // Only approved posts should be shown
      if (surfacePostsStore.postEntitiesByCid[postCid].published !== true) {
        return
      }

      populatedPages.push({
        pageType: PageType.Post,
        entityId: postCid?.replace('c', ''),
      })
    }

    const hasPosts = !isEmpty(surfacePostsStore.sortedPostCids)
    const hasGroups = !surfaceWishArrangementStore.isGroupByNone && !isEmpty(surfaceWishArrangementStore.sortedGroups)

    if (hasGroups) {
      // For each group, add group page then posts in that group
      surfaceWishArrangementStore.sortedGroups.forEach((group) => {
        // Get all posts for this group
        const allPostsInGroup = surfacePostsStore.allSortedPostsByGroup[group.id]

        const shouldShowGroup =
          !surfaceWishArrangementStore.isGroupByAuthor ||
          allPostsInGroup?.some((post) => surfacePostsStore.postEntitiesByCid[post.cid]?.published === true)

        if (shouldShowGroup) {
          pushGroupPage(group.id)
          allPostsInGroup?.forEach((post) => pushPostPage(post.cid))
        }
      })
    } else if (hasPosts) {
      // Posts only, no groups
      surfacePostsStore.sortedPostCids.forEach(pushPostPage)
    }

    return populatedPages
  })
  const currentPageNumber = computed<number | null>(() =>
    currentPageIndex.value != null ? currentPageIndex.value + 1 : null,
  )
  const currentPage = computed<Page | null>(() => {
    if (currentPageIndex.value == null) return null
    return pages.value.length > currentPageIndex.value ? pages.value[currentPageIndex.value] : null
  })
  const currentPost = computed(() => {
    if (currentPage.value?.entityId == null) return null
    return surfacePostsStore.postEntitiesByCid[getCurrentPostCid(currentPage.value.entityId)]
  })

  const currentGroup = computed(() => {
    if (currentPage.value?.entityId == null) return null
    if (currentPage.value.pageType !== PageType.Section && currentPage.value.pageType !== PageType.Group) return null

    return (
      surfaceWishArrangementStore.sortedGroups.find((group) => group.id?.toString() === currentPage.value?.entityId) ??
      null
    )
  })

  const currentPageUrl = computed(() => {
    if (currentPage.value == null) return ''
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      return buildSlideshowLink(surfaceStore.links.show, currentPage.value.pageType, currentPage.value.entityId, {
        lang: surfaceTranslationsStore.currentLanguage,
      })
    }
    return buildSlideshowLink(surfaceStore.links.show, currentPage.value.pageType, currentPage.value.entityId)
  })
  const coverPageUrl = computed(() => {
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      return buildSlideshowLink(surfaceStore.links.show, PageType.Cover, undefined, {
        lang: surfaceTranslationsStore.currentLanguage,
      })
    }
    return buildSlideshowLink(surfaceStore.links.show, PageType.Cover)
  })

  const numberOfPages = computed(() => pages.value.length)
  const lastPageIndex = computed(() => numberOfPages.value - 1)
  const prevPageIndex = computed<number | null>(() =>
    currentPageIndex.value != null ? currentPageIndex.value - 1 : null,
  )
  const nextPageIndex = computed<number | null>(() =>
    currentPageIndex.value != null ? currentPageIndex.value + 1 : null,
  )
  const isFirstPage = computed(() => currentPageIndex.value === COVER_PAGE_INDEX)
  const isLastPage = computed(() => currentPageIndex.value === numberOfPages.value - 1)
  const numberOfWordsOnPage = computed(() => {
    if (currentPage.value?.pageType === PageType.Cover) {
      return getNumberOfWords(surfaceStore.title) + getNumberOfWords(surfaceStore.description)
    } else if (
      (currentPage.value?.pageType === PageType.Section || currentPage.value?.pageType === PageType.Group) &&
      currentPage.value?.entityId !== undefined
    ) {
      if (currentGroup.value == null) {
        return 0
      }

      if (!('title' in currentGroup.value) || currentGroup.value?.title == null) return 0
      return getNumberOfWords(currentGroup.value.title)
    }

    if (currentPost.value != null) {
      return getNumberOfWords(currentPost.value.body) + getNumberOfWords(currentPost.value.subject)
    }

    return 0
  })

  /*
   * ATTACHMENTS
   */

  const postCidsForLoadedAttachments = ref(new Set<Cid>())
  const areAttachmentsLoadedForCurrentPost = computed(() => {
    if (
      currentPage.value?.pageType === PageType.Post &&
      currentPage.value?.entityId != null &&
      currentPost.value?.attachment != null &&
      currentPost.value?.attachment.length > 0
    ) {
      const currentPostCid = getCurrentPostCid(currentPage.value.entityId)
      return postCidsForLoadedAttachments.value.has(currentPostCid)
    }
    return true
  })
  const numberOfAttachmentsOnPage = computed(() => {
    if (
      currentPage.value?.pageType !== PageType.Post ||
      currentPost.value?.attachment === undefined ||
      currentPost.value?.attachment === ''
    ) {
      return 0
    }
    // at the moment we only support 1 attachment per post
    return 1
  })

  /*
   * Embed
   */

  const slideshowEmbedCodeUrlTemplate = computed<string>(
    () => getVuexStore<RootState>()?.state.constants.slideshowEmbedCodeUrlTemplate,
  )

  return {
    // Data
    isDataLoaded,
    hasLoadedPages,
    pageCoverLoaded,
    isPageCoverLoaded,

    // Custom styles
    shouldRemoveBackground,

    // Overlays
    isAnyMenuOpen,
    isAnyOverlayOpen,
    isMoreMenuOpen,
    isSharePanelOpen,
    isAutoplayMenuOpen,
    isShareViaAnotherAppPanelOpen,
    isEmbedPanelOpen,
    isLanguagePanelOpen,
    isAccessibilitySettingsModalOpen,

    // QR Code
    isQrCodeShown,
    shouldRemoveQrCode,

    // Pages
    pages,
    currentPageIndex,
    currentPost,
    currentGroup,
    currentPage,
    currentPageNumber,
    lastPageIndex,
    prevPageIndex,
    nextPageIndex,
    isFirstPage,
    isLastPage,
    numberOfPages,
    numberOfWordsOnPage,
    currentPageUrl,
    coverPageUrl,

    // Attachments
    postCidsForLoadedAttachments,
    areAttachmentsLoadedForCurrentPost,
    numberOfAttachmentsOnPage,

    // Embed
    slideshowEmbedCodeUrlTemplate,
  }
})
